import { useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import type { AssistantConversation } from '@pv/common/assistants';

interface AdminConversation extends AssistantConversation {
  organizationName?: string | null;
  venueId?: string | null;
  venueName?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  eventName?: string | null;
}

const LIST = gql`
  query AdminAssistantConversations(
    $organizationId: ID
    $venueId: ID
    $eventId: ID
    $userId: ID
    $failed: Boolean
    $since: ISO8601DateTime
    $untilTime: ISO8601DateTime
    $beforeId: ID
  ) {
    adminAssistantConversations(
      organizationId: $organizationId
      venueId: $venueId
      eventId: $eventId
      userId: $userId
      failed: $failed
      since: $since
      untilTime: $untilTime
      beforeId: $beforeId
    )
  }
`;

const ID_FILTERS = [
  ['organizationId', 'Organization ID'],
  ['venueId', 'Venue ID'],
  ['eventId', 'Event ID'],
  ['userId', 'User ID'],
] as const;
const DATE_FILTERS = [
  ['since', 'Created from'],
  ['untilTime', 'Created until'],
] as const;
const FILTER_KEYS = [...ID_FILTERS, ...DATE_FILTERS].map(([key]) => key);
type Filters = Record<(typeof FILTER_KEYS)[number], string>;

type IdFilter = (typeof ID_FILTERS)[number][0];
function readIdFilter(params: URLSearchParams): IdFilter {
  return (
    ID_FILTERS.find(([key]) => params.get(key)?.trim())?.[0] || 'organizationId'
  );
}

function readFilters(params: URLSearchParams): Filters {
  const selectedId = readIdFilter(params);
  return Object.fromEntries(
    FILTER_KEYS.map((key) => [
      key,
      key === selectedId || key === 'since' || key === 'untilTime'
        ? params.get(key) || ''
        : '',
    ])
  ) as Filters;
}

function dateErrors(filters: Filters): Partial<Filters> {
  const errors: Partial<Filters> = {};
  DATE_FILTERS.forEach(([key]) => {
    if (filters[key] && !Number.isFinite(new Date(filters[key]).getTime())) {
      errors[key] = 'Enter a valid date and time.';
    }
  });
  if (
    !Object.keys(errors).length &&
    filters.since &&
    filters.untilTime &&
    new Date(filters.since) > new Date(filters.untilTime)
  ) {
    errors.untilTime = 'End must be on or after the start.';
  }
  return errors;
}

const statusColors: Record<
  string,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  queued: 'default',
  cancelled: 'default',
  running: 'info',
  waiting: 'warning',
  paused: 'warning',
  completed: 'success',
  failed: 'error',
};

function Identity({
  name,
  id,
  fallback,
  to,
  entity,
  onUseFilter,
  onCopy,
}: {
  name?: string | null;
  id?: string;
  fallback: string;
  to?: string;
  entity: string;
  onUseFilter: (id: string) => void;
  onCopy: (id: string) => void;
}) {
  return (
    <Box sx={{ overflowWrap: 'anywhere' }}>
      <Typography variant="body2">
        {name?.trim() && to ? (
          <Link
            component={RouterLink}
            to={to}
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
          >
            {name}
          </Link>
        ) : (
          name?.trim() || fallback
        )}
      </Typography>
      {id && (
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ mt: 0.25 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            ID {id}
          </Typography>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              aria-label={`Copy ${entity} ID ${id}`}
              onClick={() => onCopy(id)}
              sx={{ p: 0.5 }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Use as filter">
            <IconButton
              size="small"
              aria-label={`Use ${entity} ID ${id} as filter`}
              onClick={() => onUseFilter(id)}
              sx={{ p: 0.5 }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}

function UpdatedTime({ value }: { value: string }) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return <>Unavailable</>;
  return (
    <Tooltip
      title={`${date.toISOString()} · ${date.toLocaleString(undefined, {
        timeZoneName: 'long',
      })}`}
    >
      <Box
        component="time"
        dateTime={date.toISOString()}
        tabIndex={0}
        sx={{ whiteSpace: 'nowrap' }}
      >
        <Typography variant="body2">
          {date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {date.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function AssistantConversationsList() {
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const applied = readFilters(params);
  const idInput = useRef<HTMLInputElement>(null);
  const [copyNotice, setCopyNotice] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);
  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopyNotice({
        message: 'Copied ID to clipboard.',
        severity: 'success',
      });
    } catch {
      setCopyNotice({
        message: 'Unable to copy ID. Please copy it manually.',
        severity: 'error',
      });
    }
  };
  const fillIdFilter = (type: IdFilter, id: string) => {
    setIdFilter(type);
    setFilters((previous) => ({
      ...previous,
      organizationId: '',
      venueId: '',
      eventId: '',
      userId: '',
      [type]: id,
    }));
    idInput.current?.focus();
  };
  const conflictingIds =
    ID_FILTERS.filter(([key]) => params.get(key)?.trim()).length > 1;
  const [idFilter, setIdFilter] = useState<IdFilter>(() =>
    readIdFilter(params)
  );
  const [filters, setFilters] = useState(() => readFilters(params));
  const [failed, setFailed] = useState(params.get('failed') === 'true');
  const [errors, setErrors] = useState<Partial<Filters>>(() =>
    dateErrors(applied)
  );
  useEffect(() => {
    const next = new URLSearchParams(search);
    setIdFilter(readIdFilter(next));
    setFilters(readFilters(next));
    setFailed(next.get('failed') === 'true');
    setErrors(dateErrors(readFilters(next)));
  }, [search]);
  useEffect(() => {
    if (!conflictingIds) return;
    const next = new URLSearchParams(search);
    const selectedId = readIdFilter(next);
    ID_FILTERS.forEach(([key]) => {
      if (key !== selectedId) next.delete(key);
    });
    next.delete('beforeId');
    setParams(next, { replace: true });
  }, [conflictingIds, search, setParams]);
  const cursors = params.getAll('beforeId');
  const invalidAppliedDates = Object.keys(dateErrors(applied)).length > 0;
  const variables: Record<string, unknown> = {
    failed: params.get('failed') === 'true',
    beforeId: cursors[cursors.length - 1] || undefined,
  };
  ID_FILTERS.forEach(([key]) => {
    if (applied[key]) variables[key] = applied[key];
  });
  if (!invalidAppliedDates)
    DATE_FILTERS.forEach(([key]) => {
      if (applied[key]) variables[key] = new Date(applied[key]).toISOString();
    });
  const { data, loading, error, refetch } = useQuery<{
    adminAssistantConversations: AdminConversation[];
  }>(LIST, {
    variables,
    skip: invalidAppliedDates || conflictingIds,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const rows = data?.adminAssistantConversations || [];
  const hasFilters =
    FILTER_KEYS.some((key) => applied[key]) || params.get('failed') === 'true';
  const clearFilters = () => {
    setIdFilter('organizationId');
    setFilters(readFilters(new URLSearchParams()));
    setFailed(false);
    setErrors({});
    setParams({});
  };
  const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = dateErrors(filters);
    // A partially entered native date field has an empty value but badInput=true.
    DATE_FILTERS.forEach(([key]) => {
      const input = event.currentTarget.elements.namedItem(
        key
      ) as HTMLInputElement;
      if (input.validity.badInput)
        nextErrors[key] = 'Enter a valid date and time.';
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const next = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      if (filters[key].trim()) next.set(key, filters[key].trim());
    });
    if (failed) next.set('failed', 'true');
    if (next.toString() === search) void refetch();
    else setParams(next);
  };
  const previousPage = () => {
    const next = new URLSearchParams(params);
    next.delete('beforeId');
    cursors.slice(0, -1).forEach((cursor) => next.append('beforeId', cursor));
    setParams(next);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Assistant conversations</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review event assistant activity and run outcomes.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={loading || invalidAppliedDates}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </Stack>
      <Paper
        component="form"
        noValidate
        onSubmit={applyFilters}
        variant="outlined"
        sx={{ p: 2, mb: 3 }}
        aria-label="Conversation filters"
      >
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Filter conversations
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '200px minmax(0, 360px)',
            },
            gap: 2,
          }}
        >
          <TextField
            select
            size="small"
            fullWidth
            label="Filter by"
            value={idFilter}
            onChange={(event) => {
              setIdFilter(event.target.value as IdFilter);
              setFilters({
                ...filters,
                organizationId: '',
                venueId: '',
                eventId: '',
                userId: '',
              });
            }}
          >
            {ID_FILTERS.map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label.replace(' ID', '')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            inputRef={idInput}
            name={idFilter}
            size="small"
            fullWidth
            label={ID_FILTERS.find(([key]) => key === idFilter)![1]}
            value={filters[idFilter]}
            onChange={(event) =>
              setFilters({ ...filters, [idFilter]: event.target.value })
            }
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(200px, 1fr)',
            },
            gap: 2,
            mt: 2,
            alignItems: 'start',
          }}
        >
          {DATE_FILTERS.map(([key, label]) => (
            <TextField
              key={key}
              name={key}
              size="small"
              fullWidth
              type="datetime-local"
              label={label}
              InputLabelProps={{ shrink: true }}
              value={filters[key]}
              error={!!errors[key]}
              helperText={errors[key]}
              onChange={(event) =>
                setFilters({ ...filters, [key]: event.target.value })
              }
            />
          ))}
          <Box>
            <FormControlLabel
              sx={{ my: 0 }}
              label="Has failed runs"
              control={
                <Checkbox
                  checked={failed}
                  onChange={(event) => setFailed(event.target.checked)}
                />
              }
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={loading}
          >
            Apply filters
          </Button>
          <Button onClick={clearFilters}>Clear filters</Button>
        </Stack>
      </Paper>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load assistant conversations.
        </Alert>
      )}
      {invalidAppliedDates && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Correct the date filters and apply them to load conversations.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">Conversations</Typography>
          <Typography variant="caption" color="text.secondary">
            Newest conversations first
          </Typography>
        </Box>
        <TableContainer>
          <Table
            aria-label="Assistant conversations"
            aria-busy={loading}
            sx={{
              minWidth: 1100,
              tableLayout: 'fixed',
              '& th, & td': { px: 2, py: 1.5 },
              '& td': { verticalAlign: 'top' },
            }}
          >
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                {[
                  'Conversation',
                  'Organization',
                  'Venue',
                  'User',
                  'Event',
                  'Latest run',
                  'Updated',
                ].map((name, index) => (
                  <TableCell
                    key={name}
                    sx={{
                      width: index === 0 ? '24%' : index === 5 ? '11%' : '13%',
                      fontWeight: 600,
                    }}
                  >
                    {name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }, (_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 7 }, (_, cell) => (
                        <TableCell key={cell}>
                          <Skeleton />
                          <Skeleton width="55%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : !error &&
                  !invalidAppliedDates &&
                  rows.map((row) => {
                    const next = new URLSearchParams(params);
                    next.set('id', row.id);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`?${next}`}
                            underline="hover"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              overflowWrap: 'anywhere',
                              fontWeight: 500,
                            }}
                            title={row.title}
                          >
                            {row.title?.trim() || 'Untitled conversation'}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Identity
                            name={row.organizationName}
                            to={`/admin/organizations/${row.organizationId}/info`}
                            id={row.organizationId}
                            entity="organization"
                            onUseFilter={(id) =>
                              fillIdFilter('organizationId', id)
                            }
                            onCopy={copyId}
                            fallback="Organization unavailable"
                          />
                        </TableCell>
                        <TableCell>
                          <Identity
                            name={row.venueName}
                            to={
                              row.venueId
                                ? `/admin/venues/${row.venueId}/info`
                                : undefined
                            }
                            id={row.venueId || undefined}
                            entity="venue"
                            onUseFilter={(id) => fillIdFilter('venueId', id)}
                            onCopy={copyId}
                            fallback="Venue unavailable"
                          />
                        </TableCell>
                        <TableCell>
                          <Identity
                            name={row.userEmail}
                            to={`/admin/users/${row.userId}`}
                            id={row.userId}
                            entity="user"
                            onUseFilter={(id) => fillIdFilter('userId', id)}
                            onCopy={copyId}
                            fallback="User unavailable"
                          />
                        </TableCell>
                        <TableCell>
                          <Identity
                            name={row.eventName}
                            id={row.contextId}
                            entity="event"
                            onUseFilter={(id) => fillIdFilter('eventId', id)}
                            onCopy={copyId}
                            fallback="Event unavailable"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={statusColors[row.status || ''] || 'default'}
                            label={
                              row.status
                                ? row.status.charAt(0).toUpperCase() +
                                  row.status.slice(1)
                                : 'No runs'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <UpdatedTime value={row.updatedAt} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              {!loading &&
                !error &&
                !invalidAppliedDates &&
                rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: 'center', py: '48px !important' }}
                    >
                      <Typography variant="subtitle1">
                        {hasFilters
                          ? 'No conversations match these filters'
                          : 'No conversations yet'}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, mb: 1 }}
                      >
                        {cursors.length
                          ? 'You have reached the end of these results.'
                          : hasFilters
                          ? 'Try a wider date range or clear the filters.'
                          : 'Event assistant conversations will appear here once started.'}
                      </Typography>
                      {hasFilters && (
                        <Button onClick={clearFilters}>Clear filters</Button>
                      )}
                      {!!cursors.length && (
                        <Button onClick={previousPage}>Previous page</Button>
                      )}
                      {!hasFilters && !cursors.length && (
                        <Button onClick={() => refetch()}>
                          Refresh conversations
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="body2" color="text.secondary" role="status">
            {loading
              ? 'Loading conversations…'
              : error || invalidAppliedDates
              ? 'Results unavailable'
              : `${rows.length} conversation${
                  rows.length === 1 ? '' : 's'
                } on this page`}
          </Typography>
          <Stack
            component="nav"
            aria-label="Conversation pagination"
            direction="row"
            spacing={1}
          >
            <Button
              disabled={loading || cursors.length === 0}
              onClick={previousPage}
            >
              Previous
            </Button>
            <Button
              disabled={
                loading || !!error || invalidAppliedDates || rows.length !== 50
              }
              onClick={() => {
                const next = new URLSearchParams(params);
                next.append('beforeId', rows[rows.length - 1].id);
                setParams(next);
              }}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Snackbar
        open={!!copyNotice}
        autoHideDuration={2500}
        onClose={() => setCopyNotice(null)}
      >
        <Alert
          severity={copyNotice?.severity}
          onClose={() => setCopyNotice(null)}
          sx={{ width: '100%' }}
        >
          {copyNotice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
